'use client';

import { useState } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { Expand } from 'lucide-react';
import { Entity } from '@/lib/api';
import { useEntityDetailSheet } from '@/lib/hooks/useEntityDetailSheet';
import { LazyImage } from './LazyImage';
import { CompactReactionButton } from './activities/CompactReactionButton';
import { useIsMobile } from '@/hooks/use-mobile';
import { ENTITY_ROLE_OPTIONS } from '@sizzlenz/shared';

interface EntityCardProps {
  entity: Entity;
  entityType: string;
  variant?: 'default' | 'compact';
  showRoles?: boolean;
}

export function EntityCard({
  entity,
  entityType,
  variant = 'default',
  showRoles = false,
}: EntityCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const isMobile = useIsMobile();
  const isCompact = variant === 'compact';
  const { showEntityDetail } = useEntityDetailSheet();

  const getEntityPath = () => {
    const identifier = entity.slug || entity.id;
    switch (entityType) {
      case 'brand':
        return `/entities/${identifier}`;
      case 'person':
        return `/people/${identifier}`;
      case 'organisation':
        return `/entities/${identifier}`;
      default:
        return `/entities/${identifier}`;
    }
  };

  const handleShowDetails = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    showEntityDetail(entity.id, entityType, { title: entity.name_1 });
  };

  const getEntityIcon = () => {
    switch (entityType) {
      case 'brand':
        return (
          <svg
            className="w-8 h-8 text-primary-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
            />
          </svg>
        );
      case 'person':
        return (
          <svg
            className="w-8 h-8 text-primary-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        );
      case 'organisation':
        return (
          <svg
            className="w-8 h-8 text-primary-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
        );
      default:
        return (
          <svg
            className="w-8 h-8 text-primary-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        );
    }
  };
  const tagsToShow = entity.tags?.filter((tag) => !tag.startsWith('@')) || [];
  return (
    <Link href={getEntityPath()} className="h-full">
      <div
        className={clsx(
          'group relative bg-white overflow-hidden hover:shadow-xl cursor-pointer border border-gray-200 h-full flex flex-col',
          isCompact ? 'rounded-xl' : 'rounded-2xl'
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative overflow-hidden bg-white">
          {entity.image ? (
            <LazyImage
              src={entity.image}
              alt={entity.name_1 || 'Entity image'}
              aspectRatio={isCompact ? '4/3' : '3/4'}
              className={clsx(
                'w-full bg-white',
                isCompact ? 'h-[140px]' : 'h-[200px]',
                isHovered && 'scale-105'
              )}
              objectFit="contain"
              fallback={
                <div
                  className={clsx(
                    'flex items-center justify-center bg-white',
                    isCompact ? 'h-[140px]' : 'h-[200px]'
                  )}
                >
                  <div className="text-center p-4">
                    {getEntityIcon()}
                    <p
                      className={clsx(
                        'text-gray-600 mt-2 font-medium',
                        isCompact ? 'text-sm' : 'text-base'
                      )}
                    >
                      {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
                    </p>
                  </div>
                </div>
              }
            />
          ) : (
            <div
              className={clsx(
                'flex items-center justify-center bg-white',
                isCompact ? 'h-[140px]' : 'h-[200px]'
              )}
            >
              <div className="text-center p-4">
                {getEntityIcon()}
                <p
                  className={clsx(
                    'text-gray-600 mt-2 font-medium',
                    isCompact ? 'text-sm' : 'text-base'
                  )}
                >
                  {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
                </p>
              </div>
            </div>
          )}
          <div
            className={clsx(
              'absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 transition-opacity duration-300'
            )}
          />
          <button
            onClick={handleShowDetails}
            className={clsx(
              'absolute rounded-full shadow-lg flex items-center justify-center cursor-pointer transition-all duration-200',
              isCompact ? 'top-2 left-2 w-7 h-7' : 'top-3 left-3 w-10 h-10',
              'opacity-100 translate-y-0',
              'bg-white/95 backdrop-blur-sm hover:bg-primary-500 hover:text-white'
            )}
            aria-label="Show details"
            title="Show details"
          >
            <Expand className={clsx(isCompact ? 'w-3 h-3' : 'w-5 h-5')} />
          </button>
          <CompactReactionButton
            objectId={entity.id.toString()}
            objectType="entity"
            variant={variant}
            title={entity.name_1 || 'Unnamed Entity'}
            userReactions={entity.userReactions || []}
            engagementSummary={
              entity.engagement || {
                reactions: { total: 0, by_type: {} },
                votes: { up: { count: 0, last_at: '' }, down: { count: 0, last_at: '' } },
              }
            }
            className={clsx(
              'absolute',
              isCompact ? 'bottom-2 left-2' : 'bottom-3 left-3',
              'opacity-100 translate-y-0'
            )}
          />
          {tagsToShow?.length > 0 && !isCompact && (
            <div className="absolute top-3 right-3 flex flex-wrap gap-1 justify-end max-w-[60%]">
              {tagsToShow.slice(0, 2).map((tag) => (
                <span
                  key={tag}
                  className="bg-white/90 backdrop-blur-sm text-gray-700 text-xs px-2 py-0.5 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className={clsx('flex-1', isCompact ? 'p-3' : 'p-4')}>
          <h3
            className={clsx(
              'font-semibold text-gray-900 line-clamp-2 mb-1 group-hover:text-primary-600 transition-colors',
              isCompact ? 'text-sm' : 'text-base'
            )}
          >
            {entity.name_1 || 'Unnamed Entity'}
          </h3>
          {entity.name_2 && (
            <p className={clsx('text-gray-600 line-clamp-1', isCompact ? 'text-xs' : 'text-sm')}>
              {entity.name_2}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2">
            {entity.status && entity.status !== 'active' && (
              <>
                <span className="text-gray-300">•</span>
                <span
                  className={clsx('text-gray-500 capitalize', isCompact ? 'text-xs' : 'text-sm')}
                >
                  {entity.status}
                </span>
              </>
            )}
          </div>
          {showRoles &&
            (() => {
              const roles = (entity.data as any)?.roles;
              if (!Array.isArray(roles) || roles.length === 0) return null;
              const roleLabels = roles
                .map((roleId: string) => ENTITY_ROLE_OPTIONS.find((r) => r.id === roleId)?.label)
                .filter(Boolean) as string[];
              if (roleLabels.length === 0) return null;
              return (
                <div className="flex flex-wrap gap-1 mt-2">
                  {roleLabels.map((label) => (
                    <span
                      key={label}
                      className="inline-flex items-center px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-medium rounded border border-blue-200"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              );
            })()}
        </div>
      </div>
    </Link>
  );
}
